import React, { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useQueryClient } from "@tanstack/react-query";
import {
  useDismissSuccessSurvey,
  useUpdateSeekingStatus,
  getGetMeQueryKey,
  getGetProfileQueryKey,
  getListProfilesQueryKey,
} from "@workspace/api-client-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useReducedMotion } from "@/hooks/use-reduced-motion";

type Step = "initial" | "network-helped";
type FoundRole = "found_role" | "taking_break" | "still_looking" | "skipped";
type NetworkHelped = "yes" | "partially" | "no";

interface OtWSuccessSurveyModalProps {
  profileId: number | null | undefined;
  open: boolean;
  onDismiss: () => void;
}

export function OtWSuccessSurveyModal({ profileId, open, onDismiss }: OtWSuccessSurveyModalProps) {
  const prefersReducedMotion = useReducedMotion();
  const queryClient = useQueryClient();
  const [step, setStep] = useState<Step>("initial");
  const [networkHelped, setNetworkHelped] = useState<NetworkHelped | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const dismissMutation = useDismissSuccessSurvey();
  const seekingMutation = useUpdateSeekingStatus();

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: getGetMeQueryKey() });
    if (profileId != null) {
      void queryClient.invalidateQueries({ queryKey: getGetProfileQueryKey(profileId) });
      void queryClient.invalidateQueries({ queryKey: getListProfilesQueryKey() });
    }
  };

  const submit = async (result: FoundRole, helped?: NetworkHelped) => {
    if (submitting) return;
    setSubmitting(true);
    try {
      await new Promise<void>((resolve, reject) => {
        dismissMutation.mutate(
          { data: { result, ...(helped ? { networkHelped: helped } : {}) } },
          { onSuccess: () => resolve(), onError: (e) => reject(e) },
        );
      });

      if (result === "still_looking" && profileId != null) {
        await new Promise<void>((resolve) => {
          seekingMutation.mutate(
            { id: profileId, data: { seeking: true } },
            { onSuccess: () => resolve(), onError: () => resolve() },
          );
        });
      }

      invalidate();
      onDismiss();
    } catch {
      setSubmitting(false);
    }
  };

  const handleFoundRole = () => setStep("network-helped");

  const handleTakingBreak = () => void submit("taking_break");

  const handleStillLooking = () => void submit("still_looking");

  const handleSkip = () => void submit("skipped");

  const handleNetworkSubmit = () => {
    if (!networkHelped) return;
    void submit("found_role", networkHelped);
  };

  const contentVariants = {
    hidden: prefersReducedMotion ? {} : { opacity: 0, y: 8, scale: 0.97 },
    visible: { opacity: 1, y: 0, scale: 1 },
    exit: prefersReducedMotion ? {} : { opacity: 0, y: 4, scale: 0.97 },
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) handleSkip(); }}>
      <DialogContent className="bg-zinc-950 border border-white/10 shadow-2xl shadow-black/60 max-w-md rounded-2xl p-0 overflow-hidden">
        <AnimatePresence mode="wait" initial={false}>
          {step === "initial" ? (
            <motion.div
              key="initial"
              variants={contentVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              transition={{ duration: 0.18, ease: "easeOut" }}
              className="p-6"
            >
              <DialogHeader className="mb-5">
                <DialogTitle className="font-display text-lg font-bold text-white leading-snug">
                  Quick check-in
                </DialogTitle>
                <DialogDescription className="text-zinc-400 text-sm leading-relaxed mt-1">
                  We noticed your Open to Work status was recently turned off. Did you land a new role?
                </DialogDescription>
              </DialogHeader>

              <div className="flex flex-col gap-2">
                <Button
                  variant="outline"
                  className="w-full justify-start text-left border-white/10 text-zinc-200 hover:text-white hover:bg-white/5 hover:border-primary/40 transition-all font-medium h-auto py-3 px-4"
                  onClick={handleFoundRole}
                  disabled={submitting}
                >
                  <span className="text-primary mr-2 shrink-0">A</span>
                  Yes, I found something!
                </Button>
                <Button
                  variant="outline"
                  className="w-full justify-start text-left border-white/10 text-zinc-200 hover:text-white hover:bg-white/5 hover:border-white/20 transition-all font-medium h-auto py-3 px-4"
                  onClick={handleTakingBreak}
                  disabled={submitting}
                >
                  <span className="text-zinc-500 mr-2 shrink-0">B</span>
                  No, just taking a break / Not looking right now.
                </Button>
                <Button
                  variant="outline"
                  className="w-full justify-start text-left border-white/10 text-zinc-200 hover:text-white hover:bg-white/5 hover:border-white/20 transition-all font-medium h-auto py-3 px-4"
                  onClick={handleStillLooking}
                  disabled={submitting}
                >
                  <span className="text-zinc-500 mr-2 shrink-0">C</span>
                  Oops, I'm still looking!
                </Button>
              </div>
              <div className="mt-4 flex justify-center">
                <button
                  onClick={handleSkip}
                  disabled={submitting}
                  className="text-xs text-zinc-500 hover:text-zinc-300 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded"
                >
                  Skip for now
                </button>
              </div>
            </motion.div>
          ) : (
            <motion.div
              key="network-helped"
              variants={contentVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              transition={{ duration: 0.18, ease: "easeOut" }}
              className="p-6"
            >
              <DialogHeader className="mb-5">
                <DialogTitle className="font-display text-lg font-bold text-white leading-snug">
                  Congratulations!
                </DialogTitle>
                <DialogDescription className="text-zinc-400 text-sm leading-relaxed mt-1">
                  Did connections on The Carry Network help you land the role?
                </DialogDescription>
              </DialogHeader>

              <div className="flex flex-col gap-2 mb-5">
                {(["yes", "partially", "no"] as NetworkHelped[]).map((v) => {
                  const labels: Record<NetworkHelped, string> = {
                    yes: "Yes, definitely",
                    partially: "Partially",
                    no: "No, found it another way",
                  };
                  const selected = networkHelped === v;
                  return (
                    <button
                      key={v}
                      onClick={() => setNetworkHelped(v)}
                      disabled={submitting}
                      className={`w-full text-left rounded-xl border px-4 py-3 text-sm font-medium transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-primary ${
                        selected
                          ? "border-primary/60 bg-primary/10 text-white"
                          : "border-white/10 text-zinc-300 hover:border-white/20 hover:bg-white/5"
                      }`}
                    >
                      {labels[v]}
                    </button>
                  );
                })}
              </div>

              <div className="flex gap-2">
                <Button
                  variant="ghost"
                  className="text-zinc-500 hover:text-zinc-300"
                  onClick={() => setStep("initial")}
                  disabled={submitting}
                >
                  Back
                </Button>
                <Button
                  className="flex-1 bg-primary hover:bg-primary/90 text-white font-semibold"
                  onClick={handleNetworkSubmit}
                  disabled={submitting || !networkHelped}
                >
                  {submitting ? "Saving..." : "Submit"}
                </Button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </DialogContent>
    </Dialog>
  );
}
